import {
  AUTHENTICATION_CONNECTION,
  AuthenticationDispatchers,
  AuthenticationState as B2XAuthState,
  IdentityProvider,
} from '@b2x/authentication-library';
import type { AppConfig } from '@dh-io-emh/config-types';
import { initializeStore } from '@seamless/store';
import { Logger } from '../utils/logger.util';
import { initializeAuthConnection, transformB2XAuthState } from './auth-service.utils';
import { AuthState } from './types';

export type AuthService = ReturnType<typeof createAuthService>;

// This whole service can be greatly simplified once we no longer need to handle the legacy SPA.
// TODO: refactor and simplify once we get rid of our legacy SPA
export const createAuthService = ({ config }: { config: AppConfig }) => {
  const tenantId = 'oneweb';
  const store = initializeStore();
  const _authState: AuthState = { isAuthenticated: false, jweToken: null, userProfile: null };
  const _callbacks = new Set<(state: AuthState) => void>();

  // If a market has no login or is executed in AEM Author mode we return a dummy mock object
  // to avoid too many if/else conditions in other parts of our application.
  if (!config.allowLogin || config.runMode === 'AUTHOR') {
    return {
      getAuthState: (): Promise<AuthState> => Promise.resolve({ ..._authState }),
      login: async () => undefined,
      logout: async () => undefined,
      register: () => undefined,
      addChangeCallback: () => undefined,
    };
  }

  const initAuthPromise = initializeAuthConnection({ config, tenantId })
    .then(() => {
      store.subscribe(AUTHENTICATION_CONNECTION, (state: B2XAuthState) => {
        const newState = transformB2XAuthState(state);

        Object.assign(_authState, newState);

        for (const cb of _callbacks) {
          cb({ ..._authState });
        }
      });
    })
    .catch((err) => {
      Logger.error('failed to initialize authentication', err);
    });

  const getAuthDispatcher = () => store.getConnectionDispatchers<AuthenticationDispatchers>(AUTHENTICATION_CONNECTION);

  // Return copy of authState to avoid mutations
  const getAuthState = (): Promise<AuthState> => initAuthPromise.then(() => ({ ..._authState }));

  const login = async (redirectUrl?: string) => {
    const dispatcher = await getAuthDispatcher();

    dispatcher.login({
      identityProvider: IdentityProvider.CIAS,
      tenantId,
      ...(redirectUrl && { loginOptions: { redirectUrl } }),
    });
  };

  const logout = async () => {
    const dispatcher = await getAuthDispatcher();

    dispatcher.logout();
  };

  const register = (redirectUrl?: string) => {
    const {
      locale,
      microservices: {
        cias: { ciasURL },
      },
    } = config;

    // b2x login lib does not support registering users so we have to do the redirect ourself
    const url = new URL(`${ciasURL}registration`);

    url.searchParams.set('tenantId', tenantId);
    url.searchParams.set('redirectUrl', redirectUrl ?? window.location.href);
    url.searchParams.set('locale', locale.replace(/-/i, '_'));

    window.location.replace(url.toString());
  };

  const addChangeCallback = (callbackFn: (state: AuthState) => void) => {
    if (!_callbacks.has(callbackFn)) {
      _callbacks.add(callbackFn);
    }

    // return a "unsubscribe" function
    return () => _callbacks.delete(callbackFn);
  };

  return { addChangeCallback, getAuthState, login, logout, register };
};
